package org.vreo.devicehub.features.core.data.appLogger

import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format
import kotlinx.datetime.format.char
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/**
 * Defines the log levels for the application's logging system.
 * Each level has an associated integer value to allow for filtering.
 *
 * @property level The integer value representing the log level's severity.
 */
enum class DebugLevel(val level: Int) {
    /** disable level, no logs will be printed */
    DISABLED(0),

    /** An informational log message. */
    INFO(1),

    /** A debug log message, typically used during development. */
    DEBUG(2),

    /** A warning log message, indicating a potential issue. */
    WARNING(3),

    /** An error log message, indicating a critical failure. */
    ERROR(4)
}

/**
 * Provides ANSI escape codes for coloring terminal text.
 * These codes are used to change the color of the log output.
 */
object AppLogColor {
    /** Resets the text color to the default. */
    const val RESET = "\u001B[0m"

    /** Sets the text color to red. */
    const val RED = "\u001B[31m"

    /** Sets the text color to green. */
    const val GREEN = "\u001B[32m"

    /** Sets the text color to light green. */
    const val LIGHT_GREEN = "\u001B[92m"

    /** Sets the text color to yellow. */
    const val YELLOW = "\u001B[33m"

    /**
     * Retrieves the appropriate color code for a given log level.
     *
     * @param level The [DebugLevel] for which to get the color.
     * @return The ANSI color code as a [String].
     */
    fun getColorForLevel(level: DebugLevel): String {
        return when (level) {
            DebugLevel.INFO -> LIGHT_GREEN
            DebugLevel.DEBUG -> GREEN
            DebugLevel.WARNING -> YELLOW
            DebugLevel.ERROR -> RED
            else -> RESET
        }
    }
}

/**
 * A simple, customizable logging utility for multiplatform Kotlin applications.
 *
 * The logger allows for filtering messages based on a set log level and formats
 * output with timestamps, log levels, and optional tags. It uses ANSI escape
 * codes for color-coded terminal output.
 */
object AppLogger {
    private var logLevel: DebugLevel = DebugLevel.INFO

    /**
     * Sets the minimum log level that will be printed to the console.
     *
     * @param level The new [DebugLevel] to set.
     */
    fun setLogLevel(level: DebugLevel) {
        logLevel = level
    }

    /**
     * Logs an informational message.
     *
     * @param message The message to be logged.
     * @param tag An optional tag to identify the log source. Defaults to `null`.
     */
    fun info(message: String, tag: String? = null) = log(DebugLevel.INFO, tag, message)

    /**
     * Logs a debug message.
     *
     * @param message The message to be logged.
     * @param tag An optional tag to identify the log source. Defaults to `null`.
     */
    fun debug(message: String, tag: String? = null) = log(DebugLevel.DEBUG, tag, message)

    /**
     * Logs a warning message.
     *
     * @param message The message to be logged.
     * @param tag An optional tag to identify the log source. Defaults to `null`.
     */
    fun warning(message: String, tag: String? = null) = log(DebugLevel.WARNING, tag, message)

    /**
     * Logs an error message.
     *
     * @param message The message to be logged.
     * @param tag An optional tag to identify the log source. Defaults to `null`.
     */
    fun error(message: String, tag: String? = null) = log(DebugLevel.ERROR, tag, message)

    /**
     * The core logging function that formats and prints the log message to the console.
     * Messages are only printed if their level is equal to or greater than the current log level.
     *
     * @param level The [DebugLevel] of the message.
     * @param tag An optional tag to identify the log source.
     * @param message The message content.
     */
    @OptIn(ExperimentalTime::class)
    private fun log(
        level: DebugLevel,
        tag: String?,
        message: String
    ) {
        if (level.level == DebugLevel.DISABLED.level) {
            return
        }

        if (level.level >= logLevel.level) {
            val localDateTime = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())

            val timeString = localDateTime.format(
                LocalDateTime.Format {
                    hour(); char(':'); minute(); char(':'); second()
                }
            )

            val levelColor = AppLogColor.getColorForLevel(level)

            val tagString =
                if (tag != null) " ${AppLogColor.RESET}[$tag]${AppLogColor.RESET}" else ""

            println("${AppLogColor.YELLOW}[$timeString] $levelColor[${level.name}]$tagString ${AppLogColor.RESET}: $message")
        }
    }
}